import { loadableReady } from '@loadable/component';
import { ComponentType } from 'react';
import { hydrateRoot } from 'react-dom/client';
import {
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';

import { setConfigOnce } from 'config/appConfigUtils';
import { RenderingClientInnerWrapper } from 'rendering/client/components/RenderingClientInnerWrapper';
import { RenderingWrapper } from 'rendering/components/RenderingWrapper';
import { renderingDeserializeHypernovaPayload } from 'rendering/hypernova/client/renderingDeserializeHypernovaPayload';
import { MainStoreCreateFromPartialState } from 'store/MainStoreCreateFromPartialState';
import { getCookieValue } from 'utils/cookies';
import { setClientHydrated } from 'zustand-stores/renderingStateStore';
import { setSsrApiData } from 'zustand-stores/ssrApiDataStore';

import { renderingBootstrapClient } from './utils/renderingBootstrapClient';
import { renderingClientPerformanceMark } from './utils/renderingClientPerformanceMark';
import { renderingLoadClientUserEnvironment } from './utils/renderingLoadClientUserEnvironment';

type Args = {
  Component: ComponentType<Record<never, never>>;
  routeObjects: RouteObject[];
  createStore: MainStoreCreateFromPartialState;
};

export async function renderSsrClient({
  Component,
  routeObjects,
  createStore,
}: Args) {
  const payload = renderingDeserializeHypernovaPayload();
  if (!payload) return;

  renderingClientPerformanceMark('Starting app');

  const {
    node,
    data: { state, config, ssrApiData },
  } = payload;

  setSsrApiData(ssrApiData);

  // Set config and global.store as soon as possible
  setConfigOnce(config);

  const store = createStore(state);
  global.store = store;

  // Setup @loadable
  renderingClientPerformanceMark('Before waiting for loadables');
  await loadableReady();
  renderingClientPerformanceMark('Done waiting for loadables');

  // Bootstrap analytics & miscellaneous
  renderingBootstrapClient();

  // Setup react-router & Idealist wrappers
  const wrappedRoutes = [
    {
      element: (
        <RenderingClientInnerWrapper>
          <Component />
        </RenderingClientInnerWrapper>
      ),
      children: routeObjects,
    },
  ];

  const router = createBrowserRouter(wrappedRoutes);

  // Hydrate
  renderingClientPerformanceMark('Starting hydration');
  hydrateRoot(
    node,
    <RenderingWrapper store={store}>
      <RouterProvider router={router} />
    </RenderingWrapper>,
  );
  renderingClientPerformanceMark('App ready');

  const hasNoCacheCookie = getCookieValue('nc');
  if (hasNoCacheCookie) renderingClientPerformanceMark('App ready (no cache)');
  else renderingClientPerformanceMark('App ready (cachable)');

  // We need to wait for the next "tick" before setting the user environment.
  //
  // If we don't do that we create a mismatch between the server and client
  // contents, causing a hydration issue and components skip the first render
  // after setting the user environment
  setTimeout(() => {
    setClientHydrated();
    renderingLoadClientUserEnvironment({ store });
  }, 1);
}
